import { ChangeEvent, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';

import { Checkbox, WithPrefixInput } from '@app/form';
import { FieldType, MultipleChoiceField } from 'queries';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  mainContainer: {
    padding: [
      0,
      theme.marginBase * 2,
      theme.marginBase * 2,
      theme.marginBase * 2,
    ],
    backgroundColor: theme.colors.white,
  },
  minMaxContainer: {
    paddingTop: theme.marginBase * 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  minMax: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  multipleAnswers: {
    borderRadius: `0 0 ${theme.borderRadius.std}px ${theme.borderRadius.std}px`,
  },
  min: {
    marginRight: theme.marginBase * 2,
  },
  toggle: {
    margin: 0,
    marginBottom: theme.marginBase,
    lineHeight: '100%',
  },
  separator: {
    width: '100%',
    backgroundColor: theme.colors.grayBorder,
    height: 1,
  },
}));

interface Props {
  field: MultipleChoiceField;
  onChange: (field: MultipleChoiceField) => any;
}

const contraintInInterval = (
  value?: number,
  min?: number | null,
  max?: number | null,
) => {
  if (value === undefined) {
    return null;
  }
  if (min !== undefined && min !== null && value < min) {
    return min;
  }
  if (max !== undefined && max !== null && value > max) {
    return max;
  }
  return value;
};

export const ScreeningFormMinMax = ({ field, onChange }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { properties, validation = {} } = field;
  const options = properties.choices || [];

  const minimumChoices = validation && validation.required ? 1 : 0;
  const maximumChoices = options.length;

  const numberMin =
    validation &&
    (validation.minSelection === null ? undefined : validation.minSelection);
  const [min, setMin] = useState(`${numberMin === undefined ? 1 : numberMin}`);
  const onMinChange = (e: ChangeEvent<HTMLInputElement>) =>
    setMin(e.currentTarget.value);

  const numberMax =
    validation &&
    (validation.maxSelection === null ? undefined : validation.maxSelection);
  const [max, setMax] = useState(`${numberMax === undefined ? '' : numberMax}`);
  const onMaxChange = (e: ChangeEvent<HTMLInputElement>) =>
    setMax(e.currentTarget.value);

  const onMinBlur = () => {
    const minNumber = min !== '' ? Number(min) : undefined;
    if (minNumber !== undefined && isNaN(minNumber)) {
      setMin(`${numberMin === undefined ? '' : numberMin}`);
      return;
    }
    const minSelection = contraintInInterval(
      minNumber,
      minimumChoices,
      validation.maxSelection,
    );
    onChange({
      ...field,
      validation: {
        ...validation,
        minSelection,
      },
    });
    setMin(`${minSelection}`);
  };

  const onMaxBlur = () => {
    const maxNumber = max !== '' ? Number(max) : undefined;
    if (maxNumber !== undefined && isNaN(maxNumber)) {
      setMax(`${numberMax === undefined ? '' : numberMax}`);
      return;
    }
    const maxSelection = contraintInInterval(
      maxNumber,
      validation.minSelection || 1,
      maximumChoices,
    );
    onChange({
      ...field,
      validation: {
        ...validation,
        maxSelection,
      },
    });
    setMax(`${maxSelection}`);
  };

  const [checkMinMax, setMinMax] = useState(!!(numberMin || numberMax));
  const onChangeCheckMinMax = () => {
    setMinMax(!checkMinMax);
    setMin('1');
    setMax('');
    onChange({
      ...field,
      validation: undefined,
    });
  };

  return (
    <div className={classes.mainContainer}>
      <div className={classes.separator} />
      <div className={classes.multipleAnswers}>
        {field.type === FieldType.MULTIPLE_CHOICE && (
          <div className={classes.minMaxContainer}>
            <Checkbox
              noMargin
              label={t(
                'screeningForm.create.questionBuilder.choiceList.options.minMax',
              )}
              checked={checkMinMax}
              onChange={onChangeCheckMinMax}
            />
            {checkMinMax && (
              <div className={classes.minMax}>
                <div className={classes.min}>
                  <WithPrefixInput
                    type="number"
                    value={min}
                    prefixText={t(
                      'screeningForm.create.questionBuilder.choiceList.options.min',
                    )}
                    min={minimumChoices}
                    noMargin
                    onChange={onMinChange}
                    onBlur={onMinBlur}
                  />
                </div>
                <div>
                  <WithPrefixInput
                    type="number"
                    value={max}
                    prefixText={t(
                      'screeningForm.create.questionBuilder.choiceList.options.max',
                    )}
                    max={maximumChoices}
                    noMargin
                    onChange={onMaxChange}
                    onBlur={onMaxBlur}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
